import React, { useEffect, useRef, useState } from "react";
import { whatsappApi } from "../services/api";
import { normalizeWhatsAppPhone } from "../utils/helpers";
import { ASSET_BASE_URL as ASSET_URL } from '../services/api';

/**
 * WhatsAppChatView
 * ─────────────────────────────────────────────────────────────────
 * Shows the full conversation (sent + received) with one phone number,
 * WhatsApp-style: outgoing bubbles green on the right, incoming bubbles
 * white on the left. Polls every 5s for new incoming messages.
 * ─────────────────────────────────────────────────────────────────
 */
export default function WhatsAppChatView({ phoneNumber, refreshKey }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversation = async (isInitial = false) => {
    if (!phoneNumber) return;
    if (isInitial) setLoading(true);
    try {
      const normalized = normalizeWhatsAppPhone(phoneNumber);
      const rawDigits = String(phoneNumber).replace(/[^0-9]/g, "");

      // 1. Fetch with standardized 91-prefixed number
      let list1 = [];
      try {
        const data1 = await whatsappApi.getConversation(normalized);
        if (Array.isArray(data1)) list1 = data1;
      } catch (e) {
        console.warn("Could not fetch conversation with normalized number:", e);
      }

      // 2. Also fetch with raw 10-digit number if different (to catch legacy records)
      let list2 = [];
      if (rawDigits && rawDigits !== normalized) {
        try {
          const data2 = await whatsappApi.getConversation(rawDigits);
          if (Array.isArray(data2)) list2 = data2;
        } catch (_) {}
      }

      // 3. Merge unique messages by id or whatsappMessageId and sort by createdAt
      const map = new Map();
      [...list1, ...list2].forEach((msg) => {
        const key = msg?.id || msg?.whatsappMessageId || (msg?.createdAt + "_" + (msg?.content || msg?.mediaUrl || ""));
        if (key && !map.has(key)) {
          map.set(key, msg);
        }
      });

      const merged = Array.from(map.values()).sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      );

      setMessages(merged);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load conversation.");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    if (!phoneNumber) return;
    fetchConversation(true);

    pollRef.current = setInterval(() => fetchConversation(false), 5000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  useEffect(() => {
    if (refreshKey !== undefined) fetchConversation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!phoneNumber) {
    return <div style={s.empty}>Select a conversation to view messages.</div>;
  }

  return (
    <div style={s.container}>
      {/* Sleek WhatsApp scrollbar styling */}
      <style>{`
        .wa-chat-scroll::-webkit-scrollbar {
          width: 5px;
        }
        .wa-chat-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .wa-chat-scroll::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.18);
          border-radius: 9999px;
        }
        .wa-chat-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      `}</style>

      <div style={s.header}>Chat with {phoneNumber}</div>

      <div className="wa-chat-scroll" style={s.scrollArea}>
        {loading && <div style={s.centerNote}>Loading conversation…</div>}
        {!loading && error && <div style={s.centerNoteError}>⚠️ {error}</div>}
        {!loading && !error && messages.length === 0 && (
          <div style={s.centerNote}>No messages yet. Say hi 👋</div>
        )}

        {!loading &&
          !error &&
          messages.map((msg, index) => <Bubble key={msg.id || msg.whatsappMessageId || index} msg={msg} />)}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}

function Bubble({ msg }) {
  const dir = String(msg?.direction || "").toUpperCase();
  const isOutbound = dir === "OUTBOUND" || dir === "OUT" || msg?.isOutbound === true;
  const mediaInfo = getMediaInfo(msg);
  const textContent = getTextContent(msg, mediaInfo);

  return (
    <div style={{ ...s.bubbleRow, justifyContent: isOutbound ? "flex-end" : "flex-start" }}>
      <div style={{ ...s.bubble, ...(isOutbound ? s.bubbleOut : s.bubbleIn) }}>
        {mediaInfo.hasMedia ? (
          renderMedia(mediaInfo, msg)
        ) : mediaInfo.type !== "text" ? (
          renderMissingMediaPlaceholder(mediaInfo.type, isOutbound)
        ) : null}

        {textContent ? (
          <div style={s.bubbleText}>{textContent}</div>
        ) : null}

        <div style={s.bubbleMeta}>
          {formatTime(msg.createdAt)}
          {isOutbound && <StatusTicks status={msg.status} />}
        </div>
      </div>
    </div>
  );
}

const resolveMediaUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }
  const base = ASSET_URL;
  return trimmed.startsWith("/") ? `${base}${trimmed}` : `${base}/${trimmed}`;
};

const getTextContent = (msg, mediaInfo) => {
  if (!msg) return "";
  
  let text = "";
  if (typeof msg.content === "string") {
    const trimmed = msg.content.trim();
    // Check if content is stringified JSON
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        text = parsed.text || parsed.body || parsed.message || parsed.caption || "";
      } catch (_) {
        text = trimmed;
      }
    } else {
      text = trimmed;
    }
  }

  if (!text) {
    text =
      msg.text ||
      msg.body ||
      msg.message ||
      msg.caption ||
      msg.buttonText ||
      msg.interactive?.button_reply?.title ||
      msg.interactive?.list_reply?.title ||
      "";
  }

  // If text is exact same as media url, don't show it as raw link text
  if (
    mediaInfo?.hasMedia &&
    (text === mediaInfo.url ||
      text === mediaInfo.rawUrl ||
      text === mediaInfo.url?.replace(ASSET_URL, ""))
  ) {
    return "";
  }

  return text;
};

const getMediaInfo = (msg) => {
  if (!msg) return { url: "", rawUrl: "", type: "text", hasMedia: false };

  let rawUrl =
    msg.mediaUrl ||
    msg.media_url ||
    msg.imageUrl ||
    msg.image_url ||
    msg.url ||
    msg.fileUrl ||
    msg.attachmentUrl ||
    msg.audioUrl ||
    msg.audio_url ||
    msg.videoUrl ||
    msg.video_url ||
    msg.documentUrl ||
    msg.document_url ||
    msg.filePath ||
    msg.s3Url ||
    msg.file ||
    msg.media ||
    msg.audio?.link ||
    msg.image?.link ||
    msg.video?.link ||
    msg.document?.link ||
    "";

  let type = String(
    msg.messageType || msg.type || msg.message_type || ""
  ).toLowerCase();

  // Normalize known types
  if (type === "voice" || type === "ptt" || type === "recording") type = "audio";
  if (type === "photo" || type === "picture") type = "image";
  if (type === "doc" || type === "pdf" || type === "file") type = "document";

  const contentStr = typeof msg.content === "string" ? msg.content.trim() : "";
  
  // Check if content itself is a JSON object string with url
  if (!rawUrl && contentStr.startsWith("{") && contentStr.endsWith("}")) {
    try {
      const parsed = JSON.parse(contentStr);
      rawUrl =
        parsed.mediaUrl ||
        parsed.url ||
        parsed.link ||
        parsed.audioUrl ||
        parsed.imageUrl ||
        parsed.videoUrl ||
        parsed.documentUrl ||
        "";
      if (parsed.type) type = String(parsed.type).toLowerCase();
    } catch (_) {}
  }

  const isImageExtension = (u) => /\.(jpeg|jpg|png|webp|gif|svg|bmp)(\?.*)?$/i.test(u);
  const isVideoExtension = (u) => /\.(mp4|3gp|mov|webm|ogg)(\?.*)?$/i.test(u);
  const isAudioExtension = (u) => /\.(mp3|aac|wav|ogg|opus|m4a|amr|webm)(\?.*)?$/i.test(u);
  const isDocExtension = (u) => /\.(pdf|docx?|xlsx?|pptx?|txt|csv)(\?.*)?$/i.test(u);

  // If content contains URL
  if (
    !rawUrl &&
    (contentStr.startsWith("http://") ||
      contentStr.startsWith("https://") ||
      contentStr.startsWith("data:image/") ||
      contentStr.startsWith("data:audio/") ||
      contentStr.startsWith("/uploads/") ||
      contentStr.startsWith("uploads/"))
  ) {
    if (
      type === "image" ||
      type === "sticker" ||
      isImageExtension(contentStr) ||
      contentStr.startsWith("data:image/")
    ) {
      rawUrl = contentStr;
      if (!type) type = "image";
    } else if (type === "video" || isVideoExtension(contentStr)) {
      rawUrl = contentStr;
      if (!type) type = "video";
    } else if (type === "audio" || isAudioExtension(contentStr) || contentStr.startsWith("data:audio/")) {
      rawUrl = contentStr;
      if (!type) type = "audio";
    } else if (type === "document" || isDocExtension(contentStr)) {
      rawUrl = contentStr;
      if (!type) type = "document";
    }
  }

  // Detect type from url extension if type is missing or generic
  if (rawUrl && (!type || type === "text" || type === "media")) {
    if (isImageExtension(rawUrl) || rawUrl.startsWith("data:image/")) {
      type = "image";
    } else if (isVideoExtension(rawUrl)) {
      type = "video";
    } else if (isAudioExtension(rawUrl) || rawUrl.startsWith("data:audio/")) {
      type = "audio";
    } else if (isDocExtension(rawUrl)) {
      type = "document";
    } else {
      type = "image";
    }
  }

  const finalUrl = resolveMediaUrl(rawUrl);

  return {
    url: finalUrl,
    rawUrl,
    type: type || (finalUrl ? "image" : "text"),
    hasMedia: !!finalUrl,
  };
};

function renderMissingMediaPlaceholder(type, isOutbound) {
  const iconMap = {
    audio: "🎙️ Voice Note",
    image: "🖼️ Photo",
    video: "🎬 Video",
    document: "📄 Document",
    sticker: "✨ Sticker",
    location: "📍 Location",
    contact: "👤 Contact",
  };
  const label = iconMap[type] || "📎 Attachment";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        background: isOutbound ? "rgba(0,0,0,0.06)" : "rgba(0,0,0,0.04)",
        borderRadius: 8,
        fontSize: 12,
        color: "#475569",
        marginBottom: 4,
        fontStyle: "italic",
      }}
    >
      <span>{label}</span>
      <span style={{ fontSize: 10, color: "#94a3b8" }}>• processing...</span>
    </div>
  );
}

function renderMedia(mediaInfo, msg) {
  const { url, type } = mediaInfo;
  if (!url) return null;

  if (type === "image" || type === "sticker") {
    return (
      <div style={{ position: "relative", marginBottom: 6 }}>
        <a href={url} target="_blank" rel="noreferrer" title="Click to view full image">
          <img
            src={url}
            alt="WhatsApp Media"
            style={{
              maxWidth: "100%",
              maxHeight: 240,
              width: "auto",
              height: "auto",
              borderRadius: 8,
              display: "block",
              cursor: "pointer",
              objectFit: "cover",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const fb = e.currentTarget.nextSibling;
              if (fb) fb.style.display = "flex";
            }}
          />
        </a>
        <div
          style={{
            display: "none",
            alignItems: "center",
            gap: 6,
            padding: "8px 12px",
            background: "#f1f5f9",
            borderRadius: 8,
            fontSize: 12,
            color: "#475569",
          }}
        >
          <span>🖼️ Image Attachment</span>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#2563eb", textDecoration: "underline", fontWeight: 600 }}
          >
            View / Download
          </a>
        </div>
      </div>
    );
  }

  if (type === "video") {
    return (
      <div style={{ marginBottom: 6 }}>
        <video
          src={url}
          controls
          style={{
            maxWidth: "100%",
            maxHeight: 240,
            borderRadius: 8,
            display: "block",
          }}
        />
      </div>
    );
  }

  if (type === "audio") {
    return (
      <div style={{ marginBottom: 4, minWidth: 220, maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 13 }}>🎙️</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>Voice Note</span>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            download
            style={{ fontSize: 10, color: "#075E54", textDecoration: "underline", fontWeight: 500 }}
            title="Download or open in new tab"
          >
            Open ↗
          </a>
        </div>
        <audio
          src={url}
          controls
          preload="metadata"
          style={{ width: "100%", height: 34, outline: "none", borderRadius: 16 }}
        >
          <source src={url} type="audio/ogg" />
          <source src={url} type="audio/mpeg" />
          <source src={url} type="audio/mp4" />
          <source src={url} type="audio/wav" />
          Your browser does not support audio playback.
        </audio>
      </div>
    );
  }

  if (type === "document") {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: "rgba(0,0,0,0.04)",
          borderRadius: 8,
          color: "#1d4ed8",
          fontSize: 13,
          textDecoration: "none",
          marginBottom: 6,
          fontWeight: 500,
        }}
      >
        <span>📄</span>
        <span style={{ textDecoration: "underline", wordBreak: "break-all" }}>
          {msg.filename || msg.content || "Download Document"}
        </span>
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noreferrer" style={s.docLink}>
      🔗 Attachment Link
    </a>
  );
}

function StatusTicks({ status }) {
  const normStatus = String(status || "").toLowerCase();
  const map = { sent: "✓", delivered: "✓✓", read: "✓✓", success: "✓✓" };
  const color = normStatus === "read" ? "#53bdeb" : "rgba(100, 116, 139, 0.8)";
  return <span style={{ marginLeft: 4, color, fontSize: 11 }}>{map[normStatus] || "✓"}</span>;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const s = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    borderRadius: 16,
    overflow: "hidden",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  header: {
    display: "none", // Managed by FloatingWhatsAppDrawer
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    padding: "12px 10px",
    background: "#efeae2",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(0, 0, 0, 0.18) transparent",
  },
  empty: {
    padding: 24,
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 13.5,
    border: "1px dashed #e2e8f0",
    borderRadius: 12,
  },
  centerNote: { textAlign: "center", color: "#94a3b8", fontSize: 13, marginTop: 20 },
  centerNoteError: { textAlign: "center", color: "#dc2626", fontSize: 13, marginTop: 20 },
  bubbleRow: { display: "flex", width: "100%" },
  bubble: {
    maxWidth: "85%",
    padding: "7px 10px",
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.4,
    boxShadow: "0 1px 1.5px rgba(0,0,0,0.13)",
  },
  bubbleOut: { background: "#dcf8c6", borderTopRightRadius: 2 },
  bubbleIn: { background: "#ffffff", borderTopLeftRadius: 2 },
  bubbleText: { whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#111827", fontSize: 13 },
  bubbleMeta: { fontSize: 10, color: "#6b7280", textAlign: "right", marginTop: 2, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 },
  mediaImg: { maxWidth: "100%", borderRadius: 8, marginBottom: 4, display: "block" },
  docLink: { color: "#1d4ed8", fontSize: 13, textDecoration: "underline", display: "block", marginBottom: 4 },
};